import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { appendParams, ObservableDataLoader } from '@bsquare/angular-common';
import { UserManagement } from '@bsquare/coke-common';
import { forkJoin, Observable } from 'rxjs';
import { delayWhen, take } from 'rxjs/operators';
import {
  createCountLoader,
  createGetLoader,
  DATA_LOADER_CONFIG_SHORT,
} from 'services/observable-data-loader-config';
import { PermissionService } from './permission.service';

export const RULES_DEBUG_PERMISSION = 'Platform.Workbench.Rules.Debugging';
const BASE_URL = '/api/v1/identity-service';

type GroupRead = UserManagement.GroupRead;
type GroupCreate = UserManagement.GroupCreate;
type GroupUpdate = UserManagement.GroupUpdate;

type UserRead = UserManagement.UserRead;
type UserUpdate = UserManagement.UserUpdate;
type UserCreate = UserManagement.UserCreate;

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly getUserLoader: ObservableDataLoader<UserRead[]>;
  private readonly getGroupLoader: ObservableDataLoader<GroupRead[]>;
  private readonly getUserCountLoader: ObservableDataLoader<number>;
  private readonly getGroupCountLoader: ObservableDataLoader<number>;
  private readonly getSingleLoader: ObservableDataLoader<any>;

  private readonly httpClient: HttpClient;
  private readonly permissionService: PermissionService;

  public constructor() {
    const httpClient = inject(HttpClient);
    const permissionService = inject(PermissionService);

    this.getUserLoader = createGetLoader(httpClient);
    this.getGroupLoader = createGetLoader(httpClient);
    this.getUserCountLoader = createCountLoader(httpClient);
    this.getGroupCountLoader = createCountLoader(httpClient);
    this.getSingleLoader = createGetLoader(httpClient, DATA_LOADER_CONFIG_SHORT);
    this.httpClient = httpClient;
    this.permissionService = permissionService;
  }

  /**
   * Creates a new user and returns the users id.
   * Clears the cached users.
   * @param user the user to create.
   */
  public createUser(user: UserCreate): Observable<string> {
    return this.httpClient.post<string>(`${BASE_URL}/users/create`, user).pipe(
      delayWhen(() =>
        forkJoin([this.getUserCountLoader.reloadAll(), this.getUserLoader.reloadAll()]),
      ),
      take(1),
    );
  }

  /**
   * Gets the users that exist in a group.
   * @param groupId the ID of the group to get the users of.
   */
  public getUsersInGroup(groupId: string): Observable<UserRead[]> {
    return this.getUserLoader.load(`${BASE_URL}/users/${groupId}`);
  }

  /**
   * Gets all the users in the database.
   */
  public getAllUsers(): Observable<UserRead[]> {
    return this.getUserLoader.load(`${BASE_URL}/users`);
  }

  /**
   * Gets a paginated array of users.
   * @param options the options to filter by.
   * @param reload whether to reload the data before loading.
   */
  public getUsers(options: Record<string, unknown>, reload = false): Observable<UserRead[]> {
    if (reload) {
      this.getUserLoader.reload(appendParams(`${BASE_URL}/users`, options));
    }
    return this.getUserLoader.load(appendParams(`${BASE_URL}/users`, options));
  }

  /**
   * Gets all groups in the DB.
   * @returns an observable containing all the groups in the DB.
   */
  public getAllGroups(): Observable<GroupRead[]> {
    return this.getGroupLoader.load(`${BASE_URL}/groups`);
  }

  /**
   * Gets a paginated list of groups as specified by the provided parameters.
   * @param options the options to filter by.
   * @param reload whether to reload the data before loading.
   */
  public getGroups(options: Record<string, unknown>, reload = false): Observable<GroupRead[]> {
    if (reload) {
      this.getGroupLoader.reload(appendParams(`${BASE_URL}/groups`, options));
    }
    return this.getGroupLoader.load(appendParams(`${BASE_URL}/groups`, options));
  }

  /**
   * Gets the total number of groups. Optionally a filter can be applied before counting.
   * @param options the options to filter by.
   * @param reload whether to reload the data before loading.
   */
  public getGroupsCount(options: Record<string, unknown>, reload = false): Observable<number> {
    if (reload) {
      this.getGroupCountLoader.reload(appendParams(`${BASE_URL}/groups`, options));
    }
    return this.getGroupCountLoader.load(appendParams(`${BASE_URL}/groups`, options));
  }

  /**
   * Gets the total number of users. Optionally a filter can be applied before counting.
   * @param options the options to filter by.
   * @param reload whether to reload the data before loading.
   */
  public getUsersCount(options: Record<string, unknown>, reload = false): Observable<number> {
    if (reload) {
      this.getUserCountLoader.reload(appendParams(`${BASE_URL}/users`, options));
    }
    return this.getUserCountLoader.load(appendParams(`${BASE_URL}/users`, options));
  }

  /**
   * Updates a group. Clears the cached groups.
   * @param groupId the id of the group to update.
   * @param updateDTO the new updated group.
   */
  public updateGroup(groupId: string, updateDTO: GroupUpdate): Observable<void> {
    return this.httpClient.patch<void>(`${BASE_URL}/groups/${groupId}`, updateDTO).pipe(
      delayWhen(() =>
        forkJoin([this.getGroupCountLoader.reloadAll(), this.getGroupLoader.reloadAll()]),
      ),
      take(1),
    );
  }

  /**
   * Deletes the specified user.
   * @param userId the id of the user to delete.
   */
  public deleteUser(userId: string): Observable<void> {
    return this.httpClient.delete<void>(`${BASE_URL}/users/${userId}`).pipe(
      delayWhen(() =>
        forkJoin([
          this.getUserCountLoader.reloadAll(),
          this.getUserLoader.reloadAll(),
          this.permissionService.reloadUserPemissions(),
        ]),
      ),
      take(1),
    );
  }

  /**
   * Updates a user. Clears the cached users.
   * @param userId the id of the user to update.
   * @param updateDTO the new updated user.
   */
  public updateUser(userId: string, updateDTO: UserUpdate): Observable<void> {
    return this.httpClient.patch<void>(`${BASE_URL}/users/${userId}`, updateDTO).pipe(
      delayWhen(() =>
        forkJoin([this.getUserCountLoader.reloadAll(), this.getUserLoader.reloadAll()]),
      ),
      take(1),
    );
  }

  /**
   * Updates the user that are in a group. Clears the cached users.
   * This completely replaces the groups users.
   * @param groupId the id of the group to update the users of.
   * @param users the users to set as the new group users.
   */
  public updateUsersInGroup(groupId: string, userIds: string[]): Observable<void> {
    return this.httpClient
      .post<void>(`${BASE_URL}/users/group/${groupId}`, { users: userIds })
      .pipe(
        delayWhen(() =>
          forkJoin([this.getUserCountLoader.reloadAll(), this.getUserLoader.reloadAll()]),
        ),
        take(1),
      );
  }

  /**
   * Creates a new group and returns it.
   * Clears the cached groups.
   * @param group the group to create.
   */
  public createGroup(group: GroupCreate): Observable<GroupRead> {
    return this.httpClient.post<GroupRead>(`${BASE_URL}/groups`, group).pipe(
      delayWhen(() =>
        forkJoin([this.getGroupCountLoader.reloadAll(), this.getGroupLoader.reloadAll()]),
      ),
      take(1),
    );
  }

  /**
   * Deletes the specified group.
   * @param groupId the id of the group to delete.
   */
  public deleteGroup(groupId: string): Observable<void> {
    return this.httpClient.delete<void>(`${BASE_URL}/groups/${groupId}`).pipe(
      delayWhen(() =>
        forkJoin([
          this.getGroupCountLoader.reloadAll(),
          this.getGroupLoader.reloadAll(),
          this.permissionService.reloadUserPemissions(),
        ]),
      ),
      take(1),
    );
  }

  public reloadGroups(): Observable<any> {
    return forkJoin([
      this.getGroupCountLoader.reloadAll(),
      this.getGroupLoader.reloadAll(),
      this.permissionService.reloadUserPemissions(),
    ]);
  }

  public reloadUsers(): Observable<any> {
    return forkJoin([this.getUserCountLoader.reloadAll(), this.getUserLoader.reloadAll()]);
  }

  public getPasswordPolicy(): Observable<Record<any, string>> {
    return this.getSingleLoader.load(appendParams(`${BASE_URL}/users/password-policy`));
  }

  public setPasswordPolicy(policy: Record<string, any>): Observable<void> {
    return this.httpClient.post<void>(`${BASE_URL}/users/password-policy`, policy).pipe(
      delayWhen(() => forkJoin([this.getSingleLoader.reloadAll()])),
      take(1),
    );
  }

  public changePassword(): Observable<void> {
    return this.httpClient.post<void>(`${BASE_URL}/users/change-password`, {});
  }

  public changeDetails(userId: string, newValues: UserUpdate): Observable<void> {
    return this.httpClient.patch<void>(`${BASE_URL}/users/modify/${userId}`, newValues).pipe(
      delayWhen(() =>
        forkJoin([this.getUserCountLoader.reloadAll(), this.getUserLoader.reloadAll()]),
      ),
      take(1),
    );
  }
}
